/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import moment from 'moment';
import { styled } from '@this/constants/themes';
import type { ArrangerArgs } from '@this/src/domain/arranger/arranger';
import Arranger from '@this/src/domain/arranger/arranger';
import type {
  OrderItemStepAction,
  OrderItemStepAssignType,
  OrderItemStepTodoResponseArgs
} from '@this/src/domain/arrangement/order_item_step_todo';
import OrderItemStepTodo, {
  convertOrderItemStepTodoResponseToArgs,
  ORDER_ITEM_STEP_ASSIGNS
} from '@this/src/domain/arrangement/order_item_step_todo';
import Notification from '@this/src/notification';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from '@this/src/components/shared/ui/data_displays/table';
import { Fetcher, HTTPError } from '@this/src/util';
import { OrderItemJobTypeNumKeys } from '@this/src/domain/order_item/order_item_job_type';
import { OrderItemStatusOngoing, OrderItemStatusAvailable2 } from '@this/src/domain/order_item/order_item_status';
import { TripStatusOngoing, TripStatusAvailable } from '@this/src/domain/trip/trip_status';
import { OrderItemCategoryKeys } from '@this/src/domain/order_item/order_item_category';
import { OrderItemStepStatusOngoing } from '@this/src/domain/order_item/order_item_step_status';
import type { TicketingTypeQueryKey } from '@this/domain/arrangement/ticketing_type_query';
import OrderItemStepTodoTableRow from './order_item_step_todo_table_row';
import useOrganizationSelector from '../use_organization_selector';
import useArrangerSelector from '../use_arranger_selector';
import type { UrlQuery } from './order_item_step_todo_search_area';
import { initialUrlQuery, OrderItemStepTodoSearchArea } from './order_item_step_todo_search_area';
import OrderItemStepArrangerUpdateSelector from './order_item_step_arranger_update_selector';
import OrderItemStepSequenceAbortConfirm from './order_item_step_sequence_abort_confirm';
import OrderItemStepWorkDialog from './order_item_step_work_dialog';

type AssignedCounts = Record<OrderItemStepAssignType, number>;

interface Props {
  serviceId: number;
}

interface Response {
  order_item_todos: OrderItemStepTodoResponseArgs[];
  total_count: number;
  total_page: number;
  arranger: ArrangerArgs;
}

const getNumberParam = (key: string): number | undefined => {
  const str = utils.getParam(key);
  return str ? parseInt(str, 10) : undefined;
};

const OrderItemStepTodoList = observer(({ serviceId }: Props) => {
  const { current: history } = useRef(useHistory());
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [arranger, setArranger] = useState<Arranger>(Arranger.empty());
  const [todos, setTodos] = useState<OrderItemStepTodo[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [page, setPage] = useState(getNumberParam('page') || 1);
  const [assignedCounts, setAssignedCounts] = useState<AssignedCounts>({
    assigned_to_me: 0,
    unassigned: 0,
    all_tasks: 0
  });
  const [urlQuery, setUrlQuery] = useState<UrlQuery>(initialUrlQuery());
  const [workDialogTodo, setWorkDialogTodo] = useState<OrderItemStepTodo | null>(null);
  const [sequenceAbortConfirmTodo, setSequenceAbortConfirmTodo] = useState<OrderItemStepTodo | null>(null);
  const [arrangerUpdatePopoverTodo, setArrangerUpdatePopoverTodo] = useState<OrderItemStepTodo | null>(null);
  const [arrangerUpdatePopoverAnchorEl, setArrangerUpdatePopoverAnchorEl] = useState<Element | null>(null);
  const [organizationIds, renderOrganizationSelector, setOrganizationIds] = useOrganizationSelector();
  const [arrangerIds, renderArrangerSelector, setArrangerIds] = useArrangerSelector();

  const { assign_type: assignType } = urlQuery;

  const setUrlQueryField = useCallback(<T extends UrlQuery>(key: keyof T, value: T[keyof T]) => {
    setUrlQuery(prev => ({ ...prev, [key]: value }));
  }, []);

  const searchParams = useMemo(() => {
    const { start_date_from, start_date_to, receive_date_from, receive_date_to, ...others } = urlQuery;
    const params = {
      page,
      ...others,
      start_date_from: start_date_from?.format('YYYY-MM-DD'),
      start_date_to: start_date_to?.format('YYYY-MM-DD'),
      receive_date_from: receive_date_from?.format('YYYY-MM-DD'),
      receive_date_to: receive_date_to?.format('YYYY-MM-DD'),
      organization_ids: organizationIds.join(','),
      arranger_ids: arrangerIds.join(',')
    };
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined) delete params[key as keyof typeof params];
    });
    return params;
  }, [page, urlQuery, organizationIds, arrangerIds]);

  const fetchTodos = useCallback(() => {
    setIsLoading(true);

    const query = Object.entries(searchParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    history.push(`/arrangement/todo_list?tab=STEP&${query}`);

    Fetcher.get<Response>('/arrangement/order_item_step_todos.json', searchParams)
      .then(res => {
        setArranger(new Arranger(res.arranger));
        setTodos(
          res.order_item_todos.map(args => new OrderItemStepTodo(convertOrderItemStepTodoResponseToArgs(args)))
        );
        setTotalCount(res.total_count);
        setTotalPage(res.total_page);
        setPage(res.total_page < page ? 1 : page);
      })
      .finally(() => setIsLoading(false));
  }, [searchParams, history, page]);

  const fetchTodosCount = useCallback(() => {
    Fetcher.get<AssignedCounts>('/arrangement/order_item_step_todos/count.json', searchParams).then(res => {
      setAssignedCounts(res);
    });
  }, [searchParams]);

  const fetchAction = useCallback(() => {
    fetchTodos();
    fetchTodosCount();
  }, [fetchTodos, fetchTodosCount]);

  useEffect(() => {
    fetchAction();
  }, [fetchAction]);

  const handleWorkDialogOpen = useCallback(
    (todo: OrderItemStepTodo) => () => {
      setWorkDialogTodo(todo);
    },
    []
  );

  const fetchTodoAction = useCallback(
    (todo: OrderItemStepTodo, actionType: OrderItemStepAction) => {
      setIsUpdating(true);
      Fetcher.post('/arrangement/order_item_step_todos/todo_action.json', {
        order_item_step_id: todo.id,
        action_type: actionType
      })
        .then(() => {
          fetchAction();
          if (actionType === 'start') handleWorkDialogOpen(todo)();
        })
        .catch(error => {
          const message =
            error instanceof HTTPError && error.response?.data.message
              ? error.response.data.message
              : '更新に失敗しました';
          Notification.error(message);
        })
        .finally(() => {
          setIsUpdating(false);
        });
    },
    [fetchAction, handleWorkDialogOpen]
  );

  const handleTodoAction = useCallback(
    (todo: OrderItemStepTodo, action: OrderItemStepAction) => {
      fetchTodoAction(todo, action);
    },
    [fetchTodoAction]
  );

  const handleMessageFinish = useCallback(
    (messageId: number) => {
      Fetcher.put(`/arrangement/messages/${messageId}/finish`, {}).then(() => fetchAction());
    },
    [fetchAction]
  );

  const handleWorkDialogClose = useCallback(
    (isFetch = false) => {
      setWorkDialogTodo(null);
      if (isFetch) fetchAction();
    },
    [fetchAction]
  );

  const handleSequenceAbortConfirmOpen = useCallback(
    (todo: OrderItemStepTodo) => () => {
      setSequenceAbortConfirmTodo(todo);
    },
    []
  );

  const handleSequenceAbortConfirmClose = useCallback(() => {
    setSequenceAbortConfirmTodo(null);
  }, []);

  const fetchAbortMasterStepSequence = useCallback(
    (todo: OrderItemStepTodo) => {
      setIsUpdating(true);
      Fetcher.post('/arrangement/order_item_step_todos/abort_master_sequence.json', {
        order_item_step_id: todo.id
      })
        .then(() => {
          fetchAction();
        })
        .catch(error => {
          const message =
            error instanceof HTTPError && error.response?.data.message
              ? error.response.data.message
              : '破棄に失敗しました';
          Notification.error(message);
        })
        .finally(() => {
          setIsUpdating(false);
        });
    },
    [fetchAction]
  );

  const handleSequenceAbortConfirmAbort = useCallback(
    (todo: OrderItemStepTodo) => {
      fetchAbortMasterStepSequence(todo);
    },
    [fetchAbortMasterStepSequence]
  );

  const handleArrangerUpdatePopoverOpen = useCallback(
    (todo: OrderItemStepTodo) => (e: React.MouseEvent) => {
      setArrangerUpdatePopoverTodo(todo);
      setArrangerUpdatePopoverAnchorEl(e.currentTarget);
    },
    []
  );

  const handleArrangerUpdatePopoverClose = useCallback(() => {
    setArrangerUpdatePopoverTodo(null);
    setArrangerUpdatePopoverAnchorEl(null);
  }, []);

  const fetchAssign = useCallback(
    (todo: OrderItemStepTodo, arranger: Arranger | null) => {
      setIsUpdating(true);
      Fetcher.post('/arrangement/order_item_step_todos/assign.json', {
        order_item_step_id: todo.id,
        assigned_to_id: arranger?.id
      })
        .then(() => {
          fetchAction();
        })
        .catch(error => {
          const message =
            error instanceof HTTPError && error.response?.data.message
              ? error.response.data.message
              : '更新に失敗しました';
          Notification.error(message);
        })
        .finally(() => {
          setIsUpdating(false);
        });
    },
    [fetchAction]
  );

  const handleArrangerUpdatePopoverChange = useCallback(
    (todo: OrderItemStepTodo, arranger: Arranger | null) => {
      fetchAssign(todo, arranger);
    },
    [fetchAssign]
  );

  const handleAssignToMe = useCallback(
    (todo: OrderItemStepTodo) => {
      fetchAssign(todo, arranger);
    },
    [fetchAssign, arranger]
  );

  const handleSameDayOrderTeamShortcut = useCallback(() => {
    const today = moment();
    const allCategoriesExceptMessage = OrderItemCategoryKeys.filter(key => key !== 'message');

    setUrlQuery(prev => ({
      ...prev,
      step_statuses: [...OrderItemStepStatusOngoing],
      job_types: [0],
      statuses: [...OrderItemStatusOngoing],
      trip_statuses: [...TripStatusOngoing],
      categories: allCategoriesExceptMessage,
      element_query: 'all',
      snooze_status: 'todo',
      waiting: 'without',
      sp: 'all',
      use_qr: 'all',
      ticketing_type: 9 as TicketingTypeQueryKey,
      trip_id: undefined,
      start_date_from: undefined,
      start_date_to: undefined,
      receive_date_from: today.clone().startOf('day'),
      receive_date_to: today.clone().endOf('day'),
      only_others: false,
      only_unchanged: false,
      only_manual: false,
      only_premium_support: false,
      only_request_form: false,
      only_support_account: false,
      only_receipt_revision_in_progress: false,
      sort_key: 'started_at',
      sort_direction: 'asc'
    }));
    setOrganizationIds?.([]);
    setArrangerIds?.([]);
    setPage(1);
  }, [setOrganizationIds, setArrangerIds]);

  const handleReceiptRevisionShortcut = useCallback(() => {
    const allCategoriesExceptMessage = OrderItemCategoryKeys.filter(key => key !== 'message');

    setUrlQuery(prev => ({
      ...prev,
      step_statuses: [...OrderItemStepStatusOngoing],
      job_types: OrderItemJobTypeNumKeys,
      statuses: [...OrderItemStatusAvailable2],
      trip_statuses: [...TripStatusAvailable],
      categories: allCategoriesExceptMessage,
      element_query: 'all',
      snooze_status: 'all',
      waiting: 'all',
      sp: 'all',
      use_qr: 'all',
      ticketing_type: 9 as TicketingTypeQueryKey,
      trip_id: undefined,
      start_date_from: undefined,
      start_date_to: undefined,
      receive_date_from: undefined,
      receive_date_to: undefined,
      only_others: false,
      only_unchanged: false,
      only_manual: false,
      only_premium_support: false,
      only_request_form: false,
      only_support_account: false,
      only_receipt_revision_in_progress: true,
      sort_key: 'started_at',
      sort_direction: 'asc'
    }));
    setOrganizationIds?.([]);
    setArrangerIds?.([]);
    setPage(1);
  }, [setOrganizationIds, setArrangerIds]);

  return (
    <>
      <SelectModel>
        {Object.entries(ORDER_ITEM_STEP_ASSIGNS).map(([key, value]) => (
          <Label key={key}>
            <input
              type="radio"
              checked={assignType === key}
              onChange={() => setUrlQueryField('assign_type', key as OrderItemStepAssignType)}
            />
            {value}
            {assignedCounts[key as OrderItemStepAssignType] >= 0 &&
              `(${assignedCounts[key as OrderItemStepAssignType]})`}
          </Label>
        ))}
      </SelectModel>
      <OrderItemStepTodoSearchArea
        urlQuery={urlQuery}
        renderOrganizationSelector={renderOrganizationSelector}
        renderArrangerSelector={renderArrangerSelector}
        setState={setUrlQuery}
        assignType={assignType}
        serviceId={serviceId}
        onSameDayOrderTeamShortcut={handleSameDayOrderTeamShortcut}
        onReceiptRevisionShortcut={handleReceiptRevisionShortcut}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div>検索結果: {totalCount}件</div>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Tripステータス</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>ステータス最終更新者</TableCell>
                <TableCell>カテゴリ</TableCell>
                <TableCell>シーケンス</TableCell>
                <TableCell>変更</TableCell>
                <TableCell>法人名</TableCell>
                <TableCell>SP</TableCell>
                <TableCell>QR</TableCell>
                <TableCell>土日祝対応</TableCell>
                <TableCell>引き継ぎ</TableCell>
                <TableCell>発券方法</TableCell>
                <TableCell nowrap>出発日</TableCell>
                <TableCell nowrap>依頼日</TableCell>
                <TableCell nowrap>購入期限日</TableCell>
                <TableCell>チャットメッセージ</TableCell>
                <TableCell nowrap>Trip ID</TableCell>
                <TableCell nowrap>Trace ID</TableCell>
                <TableCell>最新のログメッセージ</TableCell>
                <TableCell>アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {todos.map((todo, i) => (
                <OrderItemStepTodoTableRow
                  key={i}
                  arranger={arranger}
                  todo={todo}
                  loading={isUpdating}
                  onTodoAction={handleTodoAction}
                  onMessageFinish={handleMessageFinish}
                  onWorkDialogOpen={handleWorkDialogOpen}
                  onSequenceAbortOpen={handleSequenceAbortConfirmOpen}
                  onArrangerOpen={handleArrangerUpdatePopoverOpen}
                  onAssignToMe={handleAssignToMe}
                  assignType={assignType}
                  serviceId={serviceId}
                />
              ))}
            </TableBody>
          </Table>
          <TablePagination
            count={totalPage}
            page={page}
            onChange={(_e: unknown, newPage: number) => setPage(newPage)}
          />
        </>
      )}
      <OrderItemStepWorkDialog serviceId={serviceId} todo={workDialogTodo} onClose={handleWorkDialogClose} />
      <OrderItemStepSequenceAbortConfirm
        todo={sequenceAbortConfirmTodo}
        onAbort={handleSequenceAbortConfirmAbort}
        onClose={handleSequenceAbortConfirmClose}
      />
      <OrderItemStepArrangerUpdateSelector
        todo={arrangerUpdatePopoverTodo}
        anchorEl={arrangerUpdatePopoverAnchorEl}
        onChange={handleArrangerUpdatePopoverChange}
        onClose={handleArrangerUpdatePopoverClose}
      />
    </>
  );
});

const SelectModel = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

const Label = styled.label`
  margin-right: 20px;
`;

export default OrderItemStepTodoList;
